import { useEffect, useCallback, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useHomeSections } from "@/hooks/useHomeSections";
import { optimizedImage } from "@/lib/imageUrl";
// Banners servidos de /public para permitir <link rel="preload"> no index.html (LCP)
const bannerFreteGratis = "/banners/banner-frete-gratis.webp";
const bannerCrieSuaMarca = "/banners/banner-crie-sua-marca.webp";
const bannerOculosSolar = "/banners/banner-oculos-solar.webp";
const bannerLentesDesconto = "/banners/banner-lentes-desconto.webp";

// Campanha mensal automática: troca o primeiro banner conforme o mês atual
const monthlyCampaigns: Record<number, { title: string; subtitle: string; cta: string; to: string; img: string }> = {
  0:  { title: "Janeiro Branco: Saúde Visual e Mental", subtitle: "Comece o ano cuidando da visão dos seus clientes. Armações leves e lentes anti-fadiga em destaque.", cta: "Ver Coleção", to: "/catalogo", img: bannerOculosSolar },
  1:  { title: "Fevereiro: Carnaval com Estilo", subtitle: "Óculos solares vibrantes e divertidos para a estação mais animada do ano.", cta: "Ver Solares", to: "/catalogo?categoria=oculos-solar", img: bannerOculosSolar },
  2:  { title: "Março Lilás: Cuidado e Prevenção", subtitle: "Promova a saúde ocular feminina com armações elegantes e lentes de proteção.", cta: "Ver Coleção", to: "/catalogo", img: bannerCrieSuaMarca },
  3:  { title: "Abril: Ofertas de Outono", subtitle: "Renove o estoque com kits especiais e descontos exclusivos para sua óptica.", cta: "Ver Ofertas", to: "/catalogo", img: bannerLentesDesconto },
  4:  { title: "Maio Amarelo: Ver Bem é Dirigir Bem", subtitle: "A visão correta salva vidas. Ofereça óculos solar com UV400 e lentes anti-reflexo para seus clientes dirigirem com segurança.", cta: "Ver Coleção Solar", to: "/catalogo", img: bannerOculosSolar },
  5:  { title: "Junho: Festa Junina e Estilo", subtitle: "Armações festivas e óculos solares para curtir as festas juninas com proteção.", cta: "Ver Coleção", to: "/catalogo", img: bannerOculosSolar },
  6:  { title: "Julho: Férias com Visão Nítida", subtitle: "Óculos solares polarizados e lentes anti-reflexo para as viagens de férias.", cta: "Ver Solares", to: "/catalogo?categoria=oculos-solar", img: bannerOculosSolar },
  7:  { title: "Agosto Dourado: Cuidado Infantil", subtitle: "Armações infantis resistentes e lentes seguras para os pequenos clientes.", cta: "Ver Infantil", to: "/catalogo", img: bannerCrieSuaMarca },
  8:  { title: "Setembro Amarelo: Valorize a Vida", subtitle: "Apoie a campanha com lentes anti-fadiga e armações confortáveis para o dia a dia.", cta: "Ver Coleção", to: "/catalogo", img: bannerLentesDesconto },
  9:  { title: "Outubro Rosa: Cuidado com Você", subtitle: "Coleção especial em rosa: armações femininas e acessórios em destaque.", cta: "Ver Coleção", to: "/catalogo", img: bannerCrieSuaMarca },
  10: { title: "Novembro Azul: Saúde Masculina", subtitle: "Armações masculinas robustas e lentes de alta performance em promoção.", cta: "Ver Masculino", to: "/catalogo", img: bannerOculosSolar },
  11: { title: "Dezembro: Ofertas de Fim de Ano", subtitle: "Aproveite os melhores preços do ano para abastecer sua óptica para 2027.", cta: "Ver Ofertas", to: "/catalogo", img: bannerLentesDesconto },
};

const currentMonthCampaign = monthlyCampaigns[new Date().getMonth()];

const fallbackSlides = [
  currentMonthCampaign,
  { title: "Crie Sua Marca", subtitle: "Personalize armações com sua logo e identidade visual", cta: "Saiba Mais", to: "/crie-sua-marca", img: bannerCrieSuaMarca },
  { title: "Lentes com Desconto", subtitle: "Use nosso simulador online e monte o melhor orçamento", cta: "Simular Agora", to: "/simulador", img: bannerLentesDesconto },
  { title: "Frete Grátis", subtitle: "Em pedidos acima de R$ 500 para todo o Brasil", cta: "Comprar Agora", to: "/catalogo", img: bannerFreteGratis },
];

// Heurística: identifica o slide de campanha mensal salvo no CMS pelo título
// e troca pelo banner do mês atual automaticamente.
const monthlyCampaignTitles = new Set(Object.values(monthlyCampaigns).map((c) => c.title));

const localImageMap: Record<string, string> = {
  ...Object.fromEntries(Object.values(monthlyCampaigns).map((c) => [c.title, c.img])),
  "Frete Grátis": bannerFreteGratis,
  "Crie Sua Marca": bannerCrieSuaMarca,
  "Lentes com Desconto": bannerLentesDesconto,
};

export default function PromoBanner() {
  const { data: sections } = useHomeSections();
  const rawSlides = (sections?.get("banner_slides") as typeof fallbackSlides) || fallbackSlides;
  const slides = rawSlides.map((s) => ({
    ...s,
    img: localImageMap[s.title] || optimizedImage(s.img, 1200, 68) || s.img,
  }));

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 5000, stopOnInteraction: false }),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  return (
    <section
      className="relative w-full"
      aria-label="Banners promocionais"
      aria-roledescription="carrossel"
    >
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex">
          {slides.map((s, i) => (
            <div
              key={i}
              className="relative flex-[0_0_100%] min-w-0 h-64 md:h-96"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} de ${slides.length}: ${s.title}`}
            >
              <img
                src={s.img}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover"
                width={1600}
                height={600}
                decoding={i === 0 ? "sync" : "async"}
                loading={i === 0 ? "eager" : "lazy"}
                {...({ fetchpriority: i === 0 ? "high" : "auto" } as any)}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
              <div className="relative z-10 flex flex-col justify-center h-full container px-6 md:px-12">
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">{s.title}</h2>
                <p className="text-sm md:text-lg text-white/90 mb-4 max-w-md">{s.subtitle}</p>
                <Link to={s.to}>
                  <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 gap-2">
                    {s.cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex z-10" role="tablist" aria-label="Slides do carrossel">
        {slides.map((s, i) => (
          <button
            key={i}
            role="tab"
            onClick={() => scrollTo(i)}
            aria-label={s.title}
            aria-selected={i === selectedIndex}
            aria-current={i === selectedIndex ? "true" : undefined}
            className="min-h-11 min-w-11 flex items-center justify-center"
          >
            <span
              aria-hidden="true"
              className={`block w-2.5 h-2.5 rounded-full transition-all ${i === selectedIndex ? "bg-white scale-125" : "bg-white/50"}`}
            />
          </button>
        ))}
      </div>

    </section>
  );
}
